import { NavLink, useLocation } from 'react-router-dom';
import { clsx } from 'clsx';
import {
  LayoutDashboard,
  RotateCcw,
  CheckSquare,
  DollarSign,
  FileText,
  Calendar,
  Settings,
  Wallet,
} from 'lucide-react';
import { useUIStore } from '@/stores';

const navItems = [
  { path: '/', icon: LayoutDashboard, label: 'Dashboard' },
  { path: '/chore-wheel', icon: RotateCcw, label: 'Chore Wheel' },
  { path: '/tasks', icon: CheckSquare, label: 'Tasks' },
  { path: '/dates', icon: Calendar, label: 'Key Dates' },
  { path: '/transactions', icon: DollarSign, label: 'Transactions' },
  { path: '/notes', icon: FileText, label: 'Notes' },
];

export function Sidebar() {
  const location = useLocation();
  const { sidebarCollapsed, setSidebarCollapsed, sidebarOpen, toggleSidebar } = useUIStore();

  // Toggle sidebar collapsed state (desktop)
  const handleToggleCollapse = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  // Close mobile sidebar when any nav item is clicked
  const handleNavClick = () => {
    if (sidebarOpen) {
      toggleSidebar();
    }
  };

  return (
    <>
      {/* Mobile backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-20 md:hidden"
          onClick={toggleSidebar}
          aria-hidden="true"
        />
      )}

      <aside
        className={clsx(
          'fixed left-0 top-0 h-full bg-white border-r border-gray-200 z-30 flex flex-col',
          'transition-all duration-300 ease-in-out',
          // Mobile: slide in/out, always w-64 when visible
          sidebarOpen ? 'translate-x-0' : '-translate-x-full',
          'w-64',
          // Desktop: always visible, width based on collapsed state
          'md:translate-x-0',
          sidebarCollapsed ? 'md:w-16' : 'md:w-64'
        )}
      >
        {/* Desktop toggle tab - straddles sidebar edge, vertically centered */}
        <button
          onClick={handleToggleCollapse}
          className={clsx(
            'hidden md:flex items-center justify-end',
            'absolute top-1/2 -translate-y-1/2 right-[-16px]',
            'w-[33px] h-[33px] rounded-r-md',
            'bg-white cursor-pointer z-10',
            "after:content-[''] after:absolute after:right-0 after:top-0",
            'after:w-[16px] after:h-full after:rounded-r-md',
            'after:border-t after:border-r after:border-b after:border-gray-200',
            'after:pointer-events-none'
          )}
          aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {sidebarCollapsed ? (
            <span className="w-0 h-0 border-t-[6px] border-t-transparent border-b-[6px] border-b-transparent border-l-[8px] border-l-gray-500 mr-2" />
          ) : (
            <span className="w-0 h-0 border-t-[6px] border-t-transparent border-b-[6px] border-b-transparent border-r-[8px] border-r-gray-500 mr-2" />
          )}
        </button>
      {/* Logo */}
      <div className="h-16 flex items-center px-4 border-b border-gray-200 overflow-hidden">
        <div className="flex items-center gap-2 text-primary-600 whitespace-nowrap">
          <Wallet className="h-7 w-7 flex-shrink-0" />
          <span
            className={clsx(
              'text-xl font-bold transition-opacity duration-300',
              sidebarCollapsed ? 'md:opacity-0' : 'opacity-100'
            )}
          >
            PerFin
          </span>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-4 overflow-y-auto overflow-x-hidden">
        <ul className="space-y-1 px-2">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive =
              item.path === '/'
                ? location.pathname === '/'
                : location.pathname.startsWith(item.path);

            return (
              <li key={item.path}>
                <NavLink
                  to={item.path}
                  onClick={handleNavClick}
                  className={clsx(
                    'flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors overflow-hidden whitespace-nowrap',
                    isActive
                      ? 'bg-primary-50 text-primary-600'
                      : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                  )}
                >
                  <Icon className="h-5 w-5 flex-shrink-0" />
                  <span
                    className={clsx(
                      'font-medium transition-opacity duration-300',
                      sidebarCollapsed ? 'md:opacity-0' : 'opacity-100'
                    )}
                  >
                    {item.label}
                  </span>
                </NavLink>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* Settings */}
      <div className="border-t border-gray-200 p-2 overflow-hidden">
        <NavLink
          to="/settings"
          onClick={handleNavClick}
          className={clsx(
            'flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors overflow-hidden whitespace-nowrap',
            location.pathname === '/settings'
              ? 'bg-primary-50 text-primary-600'
              : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
          )}
        >
          <Settings className="h-5 w-5 flex-shrink-0" />
          <span
            className={clsx(
              'font-medium transition-opacity duration-300',
              sidebarCollapsed ? 'md:opacity-0' : 'opacity-100'
            )}
          >
            Settings
          </span>
        </NavLink>
      </div>
    </aside>
    </>
  );
}
